import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import listPlugin from '@fullcalendar/list';
import interactionPlugin from '@fullcalendar/interaction';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Loader2, ArrowLeft, Search, ChevronLeft, ChevronRight, Filter } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useIsPhone } from '../hooks/useIsPhone';
import { useAutoRefresh } from '../hooks/useAutoRefresh';
import { subscribeDataChange } from '../lib/dataBus';

const API = process.env.REACT_APP_BACKEND_URL;

const MOIS_NOMS_PREST = ['janvier', 'février', 'mars', 'avril', 'mai', 'juin', 'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre'];

const TYPE_LABELS = {
    preparation: 'Preparation',
    preparation_chargement: 'Prep.+Chargement',
    chargement: 'Chargement',
    dechargement: 'Dechargement',
    demenagement_local: 'Dem. local',
    stockage: 'Stockage',
    destockage: 'Destockage',
    route_journee: 'Route',
    livraison: 'Livraison',
};

const TYPE_COLORS = {
    preparation: '#1e3a8a',
    preparation_chargement: '#1e3a8a',
    chargement: '#2563eb',
    dechargement: '#7f1d1d',
    demenagement_local: '#2563eb',
    stockage: '#7c3aed',
    destockage: '#7c3aed',
    route_journee: '#7f1d1d',
    livraison: '#eab308',
};

const JOURS_SEMAINE = ['DIM.', 'LUN.', 'MAR.', 'MER.', 'JEU.', 'VEN.', 'SAM.'];
const MOIS_NOMS = ['janvier', 'fevrier', 'mars', 'avril', 'mai', 'juin', 'juillet', 'aout', 'septembre', 'octobre', 'novembre', 'decembre'];

// Couleur par defaut pour les prestations dont le type n'est pas dans TYPE_COLORS.
// NE PAS SUPPRIMER : utilisee par getEventColor (sa suppression a cause une
// page blanche en production - ReferenceError).
const DEFAULT_COLOR = '#2e7cd6';

function getEventColor(ev) {
    // Client annulé → fond blanc, texte barré
    if (ev.client_statut === 'annule') return '#ffffff';
    if (ev.type_prestation && TYPE_COLORS[ev.type_prestation]) return TYPE_COLORS[ev.type_prestation];
    return DEFAULT_COLOR;
}

function isEventCancelled(ev) {
    return ev.client_statut === 'annule';
}

function MobileListView({ events, searchQuery, filterType, onEventClick, isPhone, continuous }) {
    const [currentMonth, setCurrentMonth] = useState(() => {
        const now = new Date();
        return new Date(now.getFullYear(), now.getMonth(), 1);
    });
    // Mode continu : borne basse d'affichage (défaut : 1er jour du mois précédent)
    const [minDate, setMinDate] = useState(() => {
        const now = new Date();
        return new Date(now.getFullYear(), now.getMonth() - 1, 1).toISOString().split('T')[0];
    });

    const searchActive = !!searchQuery.trim();
    // Recherche = tout l'agenda ; téléphone = liste continue sans changement de mois
    const showAll = continuous || searchActive;

    const filtered = events.filter(v => {
        if (filterType && filterType !== 'all' && v.type_prestation !== filterType) return false;
        if (!searchActive) return true;
        return (v.client_nom || '').toLowerCase().includes(searchQuery.toLowerCase());
    });

    const groupedByDate = useMemo(() => {
        const groups = {};
        const year = currentMonth.getFullYear();
        const month = currentMonth.getMonth() + 1;
        const monthStr = `${year}-${String(month).padStart(2, '0')}`;
        filtered.forEach(v => {
            if (!v.date) return;
            if (showAll) {
                if (continuous && !searchActive && v.date < minDate) return;
            } else if (!v.date.startsWith(monthStr)) {
                return;
            }
            if (!groups[v.date]) groups[v.date] = [];
            groups[v.date].push(v);
        });
        Object.keys(groups).forEach(date => {
            groups[date].sort((a, b) => (a.heure || '').localeCompare(b.heure || ''));
        });
        return groups;
    }, [filtered, currentMonth, showAll, continuous, searchActive, minDate]);

    const sortedDates = Object.keys(groupedByDate).sort();
    const totalResults = sortedDates.reduce((s, d) => s + groupedByDate[d].length, 0);

    const prevMonth = () => setCurrentMonth(prev => new Date(prev.getFullYear(), prev.getMonth() - 1, 1));
    const nextMonth = () => setCurrentMonth(prev => new Date(prev.getFullYear(), prev.getMonth() + 1, 1));

    const monthLabel = `${MOIS_NOMS[currentMonth.getMonth()]} ${currentMonth.getFullYear()}`;

    // Scroll automatique vers la date du jour (ou la prochaine si pas d'events aujourd'hui) — style Google Agenda
    const todayRef = useRef(null);
    const nextDayRef = useRef(null);
    const hasAutoScrolledRef = useRef(false);
    const todayStr = new Date().toISOString().split('T')[0];

    const goToday = () => {
        if (continuous) {
            const target = todayRef.current || nextDayRef.current;
            if (target) { try { target.scrollIntoView({ behavior: 'smooth', block: 'start' }); } catch {} }
            return;
        }
        const now = new Date();
        setCurrentMonth(new Date(now.getFullYear(), now.getMonth(), 1));
    };

    useEffect(() => {
        if (searchActive) return;
        const now = new Date();
        const isCurrentMonth = continuous || (currentMonth.getFullYear() === now.getFullYear() && currentMonth.getMonth() === now.getMonth());
        if (isCurrentMonth && !hasAutoScrolledRef.current && sortedDates.length > 0) {
            const target = todayRef.current || nextDayRef.current;
            if (target) {
                hasAutoScrolledRef.current = true;
                requestAnimationFrame(() => {
                    try { target.scrollIntoView({ behavior: 'auto', block: 'start' }); } catch {}
                });
            }
        }
        if (!isCurrentMonth) hasAutoScrolledRef.current = false;
    }, [currentMonth, sortedDates.length, continuous, searchActive]);

    const upcomingDateStr = sortedDates.find(d => d >= todayStr);

    return (
        <div data-testid="mobile-prestation-list">
            {searchActive ? (
                <div className="mb-4 px-1">
                    <h2 className="text-base font-semibold text-slate-800" data-testid="mobile-search-results-count">
                        {totalResults} résultat{totalResults > 1 ? 's' : ''} dans tout l'agenda
                    </h2>
                </div>
            ) : continuous ? (
                <div className="flex items-center justify-between mb-3 px-1">
                    <button onClick={goToday} className="px-3 py-1 text-sm font-medium rounded-md bg-[#2e7cd6] text-white" data-testid="mobile-today-continu">Aujourd'hui</button>
                    <button
                        onClick={() => setMinDate(prev => {
                            const d = new Date(prev + 'T00:00:00');
                            d.setMonth(d.getMonth() - 2);
                            return d.toISOString().split('T')[0];
                        })}
                        className="text-xs font-medium text-[#2e7cd6] px-3 py-1.5 rounded-md border border-blue-200 bg-blue-50"
                        data-testid="mobile-load-older"
                    >
                        Mois précédents
                    </button>
                </div>
            ) : (
                <div className="flex items-center justify-between mb-4 px-1">
                    <div className="flex items-center gap-2">
                        <button onClick={prevMonth} className="p-1.5 rounded-md hover:bg-slate-100"><ChevronLeft size={20} className="text-slate-600" /></button>
                        <button onClick={nextMonth} className="p-1.5 rounded-md hover:bg-slate-100"><ChevronRight size={20} className="text-slate-600" /></button>
                        <button onClick={goToday} className="px-3 py-1 text-sm font-medium rounded-md bg-[#2e7cd6] text-white">Aujourd'hui</button>
                    </div>
                    <h2 className="text-lg font-semibold text-slate-800 capitalize">{monthLabel}</h2>
                </div>
            )}

            {sortedDates.length === 0 ? (
                <div className="text-center py-12 text-slate-400">{searchActive ? 'Aucun résultat dans tout l\'agenda' : showAll ? 'Aucune prestation' : 'Aucune prestation ce mois'}</div>
            ) : (
                <div className="divide-y divide-slate-200">
                    {sortedDates.map((dateStr, idx) => {
                        const d = new Date(dateStr + 'T00:00:00');
                        const dayName = JOURS_SEMAINE[d.getDay()];
                        const dayNum = d.getDate();
                        const list = groupedByDate[dateStr];
                        const isToday = dateStr === todayStr;
                        const isUpcoming = !isToday && dateStr === upcomingDateStr;
                        const monthKey = dateStr.slice(0, 7);
                        const showMonthHeader = showAll && (idx === 0 || sortedDates[idx - 1].slice(0, 7) !== monthKey);

                        return (
                            <div key={dateStr}>
                                {showMonthHeader && (
                                    <div className="sticky top-0 z-10 px-3 py-1.5 bg-blue-50 border-y border-blue-100 text-sm font-bold text-slate-700 capitalize" data-testid={`month-header-${monthKey}`}>
                                        {MOIS_NOMS[d.getMonth()]} {d.getFullYear()}
                                    </div>
                                )}
                                <div ref={isToday ? todayRef : (isUpcoming ? nextDayRef : undefined)} className={`flex gap-3 ${isPhone ? 'py-2' : 'py-3'} px-1`}>
                                <div className={`${isPhone ? 'w-10' : 'w-14'} shrink-0 text-center pt-1`}>
                                    <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide">{dayName}</p>
                                    <p className={`text-2xl font-bold leading-tight ${isToday ? 'text-[#2e7cd6]' : 'text-slate-800'}`}>{dayNum}</p>
                                </div>
                                <div className="flex-1 space-y-1.5 min-w-0">
                                    {(() => {
                                        // Mêmes règles que la vue desktop (FullCalendar) :
                                        // on EXCLUT les prestations annulées du total du jour.
                                        const prestations = list.filter(e => e.event_type !== 'livraison' && !isEventCancelled(e));
                                        const totalDem = prestations.reduce((s, e) => s + (e.nb_demenageurs || 0), 0);
                                        const totalC20 = prestations.reduce((s, e) => s + (e.camion_20m3 || 0), 0);
                                        const totalC30 = prestations.reduce((s, e) => s + (e.camion_30m3 || 0), 0);
                                        if (prestations.length > 0) {
                                            return <div className="w-full rounded-md px-3 py-1.5 bg-white border-2 border-[#2e7cd6] text-slate-900 text-xs font-semibold">{totalDem} dem. | {totalC20}x20m3 | {totalC30}x30m3</div>;
                                        }
                                        return null;
                                    })()}
                                    {list.map(ev => {
                                        const cancelled = isEventCancelled(ev);
                                        const typeLabel = ev.type_prestation ? (TYPE_LABELS[ev.type_prestation] || ev.type_prestation) : '';
                                        const cancelledStrike = cancelled ? { textDecoration: 'line-through', textDecorationColor: '#000', textDecorationThickness: '1px' } : undefined;
                                        const isLivSupp = typeof ev.id === 'string' && ev.id.includes('_supp_');
                                        const clientNomDisplay = `${ev.client_nom || 'Client'}${isLivSupp ? ' (Liv supp)' : ''}`;
                                        return (
                                            <button
                                                key={ev.id}
                                                type="button"
                                                className={`w-full text-left rounded-md px-3 ${isPhone ? 'py-1.5' : 'py-2'} cursor-pointer hover:brightness-110 transition ${cancelled ? 'border border-slate-300' : ''}`}
                                                style={{ backgroundColor: getEventColor(ev) }}
                                                onClick={() => onEventClick && onEventClick(ev)}
                                                data-testid={`mobile-event-${ev.id}`}
                                            >
                                                {isPhone ? (
                                                    <>
                                                        <p className={`text-sm font-medium flex items-baseline gap-2 min-w-0 ${cancelled ? 'text-slate-700' : 'text-white'}`} style={cancelledStrike}>
                                                            <span className="tabular-nums shrink-0">{ev.heure}</span>
                                                            <span className="truncate">{clientNomDisplay}</span>
                                                        </p>
                                                        <p className={`text-[11px] leading-tight ${cancelled ? 'text-slate-600' : 'text-white/80'}`} style={cancelledStrike}>
                                                            {typeLabel}{cancelled ? ' — Annulée' : ''}
                                                        </p>
                                                    </>
                                                ) : (
                                                    <>
                                                        <p className={`text-sm font-medium truncate ${cancelled ? 'text-slate-700' : 'text-white'}`} style={cancelledStrike}>{clientNomDisplay}</p>
                                                        <p className={`text-xs ${cancelled ? 'text-slate-600' : 'text-white/80'}`} style={cancelledStrike}>{ev.heure} {typeLabel ? `- ${typeLabel}` : ''}{cancelled ? ' — Annulée' : ''}</p>
                                                    </>
                                                )}
                                            </button>
                                        );
                                    })}
                                </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}

export default function CalendrierPrestationPage() {
    const navigate = useNavigate();
    const isPhone = useIsPhone();
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [filterType, setFilterType] = useState('all');
    const [isMobile, setIsMobile] = useState(window.innerWidth < 1024);
    const [currentTitle, setCurrentTitle] = useState('');
    const [visibleRange, setVisibleRange] = useState({ start: null, end: null });
    const calendarRef = useRef(null);

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth < 1024);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const fetchEvents = useCallback(async () => {
        try {
            const { data } = await axios.get(`${API}/api/prestations-calendar`, { withCredentials: true });
            setEvents(data);
        } catch { toast.error('Erreur de chargement'); }
        finally { setLoading(false); }
    }, []);

    useEffect(() => { setLoading(true); fetchEvents(); }, [fetchEvents]);

    // Rafraîchissement silencieux : la programmation modifiée ailleurs (fiche client)
    // apparaît sans F5 — polling adaptatif + broadcast cross-onglets.
    useAutoRefresh(fetchEvents);
    useEffect(() => {
        const unsub = subscribeDataChange(['client'], () => fetchEvents());
        return unsub;
    }, [fetchEvents]);

    const filteredEvents = events.filter(ev => {
        if (filterType !== 'all' && ev.type_prestation !== filterType) return false;
        if (!searchQuery.trim()) return true;
        return (ev.client_nom || '').toLowerCase().includes(searchQuery.toLowerCase());
    });

    const searchActive = !!searchQuery.trim();

    const calendarEvents = (() => {
        const evts = filteredEvents.map(ev => {
            const dateTime = ev.heure ? `${ev.date}T${ev.heure}` : ev.date;
            const cancelled = isEventCancelled(ev);
            const bgColor = getEventColor(ev);
            const typeLabel = TYPE_LABELS[ev.type_prestation] || ev.type_prestation || '';
            const isLivSupp = typeof ev.id === 'string' && ev.id.includes('_supp_');
            const clientNomDisplay = `${ev.client_nom || 'Client'}${isLivSupp ? ' (Liv supp)' : ''}`;
            return {
                id: ev.id,
                title: `${clientNomDisplay}${typeLabel ? ' - ' + typeLabel : ''}${cancelled ? ' — Annulée' : ''}`,
                start: dateTime,
                backgroundColor: bgColor,
                borderColor: cancelled ? '#94a3b8' : bgColor,
                textColor: cancelled ? '#475569' : 'white',
                order: 1,
                classNames: cancelled ? ['event-cancelled'] : [],
                extendedProps: { cancelled, client_id: ev.client_id, remarque: ev.remarque || '', is_liv_supp: isLivSupp },
            };
        });

        // Exclude cancelled events from daily totals
        const byDate = {};
        filteredEvents.forEach(ev => {
            if (!ev.date || ev.event_type === 'livraison') return;
            if (isEventCancelled(ev)) return;
            if (!byDate[ev.date]) byDate[ev.date] = { dem: 0, c20: 0, c30: 0 };
            byDate[ev.date].dem += (ev.nb_demenageurs || 0);
            byDate[ev.date].c20 += (ev.camion_20m3 || 0);
            byDate[ev.date].c30 += (ev.camion_30m3 || 0);
        });
        // Generate all dates in visible range
        if (visibleRange.start && visibleRange.end) {
            const d = new Date(visibleRange.start);
            const end = new Date(visibleRange.end);
            while (d < end) {
                const dateStr = d.toISOString().split('T')[0];
                const totals = byDate[dateStr] || { dem: 0, c20: 0, c30: 0 };
                evts.push({
                    id: `summary_${dateStr}`,
                    title: `${totals.dem} dem. | ${totals.c20}x20m3 | ${totals.c30}x30m3`,
                    start: dateStr,
                    allDay: true,
                    backgroundColor: '#ffffff',
                    borderColor: '#2e7cd6',
                    textColor: '#0f172a',
                    order: -1,
                    display: 'block',
                    classNames: ['event-summary'],
                    extendedProps: { isSummary: true, dem: totals.dem, c20: totals.c20, c30: totals.c30 },
                });
                d.setDate(d.getDate() + 1);
            }
        }

        return evts;
    })();

    const handleEventClick = (clickInfo) => {
        const props = clickInfo.event.extendedProps || {};
        if (props.isSummary) return; // Ne rien faire sur les cases de calcul totaux
        // Privilegier le client_id directement attache a l'evenement (fiable pour livraisons supp)
        if (props.client_id) {
            navigate(`/clients/${props.client_id}`, { state: { from: 'calendrier-prestation' } });
            return;
        }
        const eventId = clickInfo.event.id || '';
        // id format: `${client_id}_${jour}` (prestation), `livraison_${client_id}` (livraison principale)
        // ou `livraison_${client_id}_supp_${supp_id}` (livraison supplementaire)
        let clientId = '';
        if (eventId.startsWith('livraison_')) {
            const rest = eventId.replace('livraison_', '');
            clientId = rest.split('_supp_')[0];
        } else if (eventId.includes('_')) {
            clientId = eventId.split('_')[0];
        }
        if (clientId) {
            navigate(`/clients/${clientId}`, { state: { from: 'calendrier-prestation' } });
        }
    };

    const renderEventContent = (eventInfo) => {
        const time = eventInfo.timeText;
        const title = eventInfo.event.title;
        const props = eventInfo.event.extendedProps || {};
        if (props.isSummary) {
            const tooltip = `Total du jour\nNombre demenageurs : ${props.dem}\nNombre camion 20m3 : ${props.c20}\nNombre camion 30m3 : ${props.c30}`;
            return (
                <div className="px-1 py-0.5 text-xs font-medium overflow-hidden whitespace-nowrap cursor-default" title={tooltip} data-testid="summary-event">
                    <span>{title}</span>
                </div>
            );
        }
        return (
            <div className="flex items-center gap-0 px-1 py-0.5 text-xs font-normal overflow-hidden whitespace-nowrap">
                <span>{time}</span>
                <span className="mx-1 opacity-60">|</span>
                <span className="truncate">{title}</span>
            </div>
        );
    };

    return (
        <div className="flex flex-col h-[calc(100vh-64px)]" data-testid="calendrier-prestation-page">
            {/* Header */}
            <div className="shrink-0 p-3 md:p-4 border-b bg-white">
                <div className="flex items-center justify-between gap-2 flex-wrap mb-3">
                    <div className="flex items-center gap-2">
                        <Button variant="outline" size="sm" onClick={() => navigate('/clients')} data-testid="cal-prest-back-btn">
                            <ArrowLeft size={16} className="mr-1" />
                            <span className="hidden sm:inline">Retour</span>
                        </Button>
                        <h1 className="text-lg md:text-xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }} data-testid="cal-prest-title">
                            Calendrier des prestations
                        </h1>
                    </div>
                </div>
                <div className="flex items-center gap-3">
                    <div className="relative flex-1 max-w-sm">
                        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                        <Input placeholder="Rechercher un client..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="pl-10" data-testid="cal-prest-search" />
                    </div>
                    <Select value={filterType} onValueChange={setFilterType}>
                        <SelectTrigger className="w-[200px]" data-testid="cal-prest-filter">
                            <Filter size={14} className="mr-2 text-slate-400" />
                            <SelectValue placeholder="Tous les types" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">Tous les types</SelectItem>
                            {Object.entries(TYPE_LABELS).map(([val, label]) => (
                                <SelectItem key={val} value={val}>
                                    <div className="flex items-center gap-2">
                                        <span className="w-2.5 h-2.5 rounded-sm shrink-0" style={{ backgroundColor: TYPE_COLORS[val] }} />
                                        {label}
                                    </div>
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
            </div>

            {/* Navigation bar — desktop uniquement, masquée pendant une recherche (résultats sur tout l'agenda) */}
            {!searchActive && (
            <div className="hidden lg:flex shrink-0 items-center gap-3 px-4 py-2.5 bg-blue-50 border-b border-blue-200">
                <button onClick={() => { if (calendarRef.current) calendarRef.current.getApi().prev(); }} className="p-2.5 rounded-full bg-white border border-slate-300 hover:bg-slate-100 hover:border-slate-400 transition-all shadow-sm" data-testid="cal-prest-prev">
                    <ChevronLeft size={22} className="text-slate-700" />
                </button>
                <button onClick={() => { if (calendarRef.current) calendarRef.current.getApi().next(); }} className="p-2.5 rounded-full bg-white border border-slate-300 hover:bg-slate-100 hover:border-slate-400 transition-all shadow-sm" data-testid="cal-prest-next">
                    <ChevronRight size={22} className="text-slate-700" />
                </button>
                <button onClick={() => { if (calendarRef.current) calendarRef.current.getApi().today(); }} className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-[#2e7cd6] text-white hover:bg-[#2568b8] transition-colors" data-testid="cal-prest-today">
                    Aujourd'hui
                </button>
                <p className="text-base font-bold text-slate-800 capitalize" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    {currentTitle}
                </p>
            </div>
            )}

            {/* Calendar */}
            <div className="flex-1 overflow-auto">
                {loading ? (
                    <div className="flex items-center justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-[#2e7cd6]" /></div>
                ) : isMobile ? (
                    <div className="p-4">
                        <MobileListView
                            events={events}
                            searchQuery={searchQuery}
                            filterType={filterType}
                            isPhone={isPhone}
                            continuous={isPhone}
                            onEventClick={(ev) => {
                                const cid = ev.client_id;
                                if (cid) navigate(`/clients/${cid}`, { state: { from: 'calendrier-prestation' } });
                            }}
                        />
                    </div>
                ) : searchActive ? (
                    /* Recherche sur TOUT l'agenda (desktop) : liste des résultats toutes dates */
                    <div className="p-4 max-w-3xl">
                        <MobileListView
                            events={events}
                            searchQuery={searchQuery}
                            filterType={filterType}
                            isPhone={false}
                            continuous={false}
                            onEventClick={(ev) => {
                                const cid = ev.client_id;
                                if (cid) navigate(`/clients/${cid}`, { state: { from: 'calendrier-prestation' } });
                            }}
                        />
                    </div>
                ) : (
                    <div className="p-4 cal-prestation">
                        <Card className="border border-slate-200 shadow-sm">
                            <CardContent className="p-4" data-testid="cal-prest-container">
                                <FullCalendar
                                    ref={calendarRef}
                                    plugins={[dayGridPlugin, listPlugin, interactionPlugin]}
                                    initialView="rollingWeeks"
                                    initialDate={new Date()}
                                    views={{
                                        rollingWeeks: {
                                            type: 'dayGrid',
                                            duration: { weeks: 5 },
                                        }
                                    }}
                                    headerToolbar={false}
                                    locale="fr"
                                    firstDay={1}
                                    events={calendarEvents}
                                    eventContent={renderEventContent}
                                    eventClick={handleEventClick}
                                    eventClassNames={(arg) => arg.event.extendedProps?.isSummary ? ['cursor-default'] : ['cursor-pointer']}
                                    datesSet={(info) => {
                                        const start = info.start;
                                        const end = new Date(info.end);
                                        end.setDate(end.getDate() - 1);
                                        const startMonth = MOIS_NOMS_PREST[start.getMonth()];
                                        const endMonth = MOIS_NOMS_PREST[end.getMonth()];
                                        if (start.getMonth() === end.getMonth()) {
                                            setCurrentTitle(`${startMonth} ${start.getFullYear()}`);
                                        } else {
                                            setCurrentTitle(`${startMonth} - ${endMonth} ${end.getFullYear()}`);
                                        }
                                        setVisibleRange({ start: info.start, end: info.end });
                                    }}
                                    height="auto"
                                    dayMaxEvents={false}
                                    eventDisplay="block"
                                    eventTimeFormat={{ hour: '2-digit', minute: '2-digit', hour12: false }}
                                    eventOrder="order"
                                    noEventsText="Aucune prestation"
                                />
                            </CardContent>
                        </Card>
                    </div>
                )}
            </div>

            {/* Legende types (cachée sur mobile/tablette) */}
            {!isMobile && (
            <div className="shrink-0 px-4 py-3 bg-white border-t border-slate-200" data-testid="cal-prest-legende">
                <div className="flex items-center gap-4 flex-wrap">
                    <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Legende</span>
                    {Object.entries(TYPE_COLORS).map(([type, color]) => (
                        <div key={type} className="flex items-center gap-1.5">
                            <span className="w-3 h-3 rounded-sm shrink-0" style={{ backgroundColor: color }} />
                            <span className="text-xs text-slate-600">{TYPE_LABELS[type]}</span>
                        </div>
                    ))}
                    <div className="flex items-center gap-1.5">
                        <span className="w-3 h-3 rounded-sm shrink-0 border border-slate-300" style={{ backgroundColor: '#ffffff' }} />
                        <span className="text-xs text-slate-600 line-through">Annulée</span>
                    </div>
                </div>
            </div>
            )}
        </div>
    );
}
